import * as THREE from 'three';
import Grid from '../Geodata/grid.js';
import { create2DPointsShape } from './ShapeBuilder.js';
import { VisualizationElement, VisualizationElements } from './VisualizationElements.js';

// Store Zustand
import FVector from '@data/geometry/FVector.js';
import { SensorPoint } from 'Simulations/SensorPoint.js';
import { getViewer } from 'store/viewerStore';
import { VisualizationElementId } from '@rendering/types/VisualizationElements';

/////////////
// Shaders //
/////////////

let voxelVertexShader = `precision highp float;

	uniform mat4 modelViewMatrix;
	uniform mat4 projectionMatrix;
	uniform vec4 instR; 

	attribute vec3 position; 
	attribute vec3 instT;
	attribute vec3 instC; 
	attribute float instS; 

	varying vec3 outColor; 

	void main() {
		outColor = instC; 
		vec3 pos = position; 

		pos += 2.0 * cross( instR.xyz, cross( instR.xyz, pos ) + instR.w * pos );
		pos *= vec3(instS); 
		pos += instT;

		gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
	}`;

let voxelFragmentShader = `precision highp float;
	uniform float opacity; 
	varying vec3 outColor; 
	void main() {
		gl_FragColor = vec4(outColor, opacity);
	}`;

let vertexShader = `precision highp float;

	uniform mat4 modelViewMatrix;
	uniform mat4 projectionMatrix;
	uniform bool Spin2;
	
	attribute vec3 position;
	attribute vec3 instT;
	attribute vec3 instC;
	attribute float instS;
	attribute vec3 instDir;
	attribute float coloredFace;
	
	varying vec3 outColor;
	varying float vColoredFace;
	
	mat3 lookAtPoint(vec3 dir) {
	  vec3 localUp = vec3(0, 0, 1);
	  vec3 fwd = normalize(dir);
	  vec3 right = normalize(cross(localUp, fwd));
	  vec3 up = normalize(cross(fwd, right));
	  return mat3(right, up, fwd);
	}

	vec3 rotateCustomAxis(vec3 pos, vec3 axis, float angle) {
		float cosAngle = cos(angle);
		float sinAngle = sin(angle);
		float oneMinusCosAngle = 1.0 - cosAngle;
		
		// Normalize the axis
		axis = normalize(axis);
		
		// Rodrigues' rotation formula
		return pos * cosAngle + cross(axis, pos) * sinAngle + axis * dot(axis, pos) * oneMinusCosAngle;
	}
	
	float angleBetween(vec3 a, vec3 b) {
		if (all(equal(a, vec3(0.0))) || all(equal(b, vec3(0.0)))) {
			return 0.0;
		}
	
		float dotProduct = dot(a, b);
		float magA = length(a);
		float magB = length(b);
		float cosine = dotProduct / (magA * magB);
	
		cosine = clamp(cosine, -1.0, 1.0); // Ensure the cosine is in range [-1, 1] to avoid NaN errors
	
		return acos(cosine);
	}
	
	void main() {
	  outColor = instC;
	  vColoredFace = coloredFace; // Colored box face
	
	  vec3 pos = position;
	
	  // Rotation based on Spin2 On/Off:
	  if (Spin2) {
		// Free rotation: 
		pos = lookAtPoint(instDir) * pos;
	  } else {
		// Spin 1
		vec3 e3 = vec3(0,0,1);
		vec3 rotAxisS = cross(e3,instDir);
		float rotAngleS = angleBetween(e3,instDir);
		pos = rotateCustomAxis(pos,rotAxisS,rotAngleS);
	  }
	  
	  pos *= vec3(instS);
	  pos += instT;
	
	  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
	}`;

let boxFragmentShader = `precision highp float;
  
	uniform float opacity; 
	uniform bool multi; 
  
	varying vec3 outColor; 
	varying float vColoredFace; // Received from vertex shader
  
	void main() {
	  bool isBlack = outColor.x == 0.0 && outColor.y == 0.0 && outColor.z == 0.0; 
	  if (vColoredFace < 0.5 && !isBlack) {
		gl_FragColor = vec4(0.78, 0.78, 0.78, opacity); // Other Faces (Gray)
	  } else if(!isBlack) {
		gl_FragColor = vec4(outColor.x, outColor.y, outColor.z, opacity); // 0.98, 0.98, 0.74, opacity); // Front Face (Direction Color)
	  } else {
        gl_FragColor = vec4(0.0, 0.0, 0.0, opacity);
      }

	  if(multi){
		gl_FragColor = vec4(outColor, opacity); 
	  }
	}`;

export class LayerDraw {
    private readonly sensorCount: number;
    private currentVisElementId: number = -1;
    private elementScaleFactor: number = 1;
    private directionlessVisElementId: number = -1;
    private directionlessElementScaleFactor: number = 1;
    private noStrokes: boolean = false;
    private sensorsVisible: boolean = true;

    // Main meshes
    private fillMesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial>;
    private strokesMesh: THREE.LineSegments<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial>;
    private sensorMesh: THREE.Group;

    private dotMesh: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
    private directionlessSphereMesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial>;
    private directionlessStrokesMesh: THREE.LineSegments<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial>;

    constructor(visualizationElement: VisualizationElement) {
        this.sensorCount = Grid.sensorPointsN;
        this.elementScaleFactor = visualizationElement.scaleFactor ?? 1;

        // Initialize meshes:
        this.dotMesh = this.createDotMesh();
        const material = this.createMaterial();
        this.fillMesh = this.createSensorsMesh(visualizationElement.geometry, material);
        this.strokesMesh = this.createStrokesMesh(visualizationElement, material);
        this.sensorMesh = new THREE.Group();
        this.sensorMesh.add(this.fillMesh);
        this.sensorMesh.add(this.strokesMesh);

        // Update shader based on visualization element:
        this.updateShaders(visualizationElement.id);
        this.currentVisElementId = visualizationElement.id;

        // Directionless meshes (spheres + optional strokes):
        this.directionlessVisElementId = visualizationElement.id;
        this.directionlessElementScaleFactor = visualizationElement.scaleFactor ?? 1;
        this.directionlessSphereMesh = this.createDirectionlessSphereMesh(visualizationElement);
        this.directionlessStrokesMesh = this.createDirectionlessStrokesMesh(visualizationElement);

        // Add to scene
        getViewer().scene.add(this.dotMesh, this.sensorMesh, this.directionlessSphereMesh, this.directionlessStrokesMesh);
    }

    public hide() {
        getViewer().scene.remove(this.dotMesh, this.sensorMesh, this.directionlessSphereMesh, this.directionlessStrokesMesh);
    }
    public show() {
        getViewer().scene.add(this.dotMesh, this.sensorMesh, this.directionlessSphereMesh, this.directionlessStrokesMesh);
    }

    private createDotMesh(): THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
        const positions = Grid.notNullSensorPoints
            .map((sensor: SensorPoint) => sensor.getCenter())
            .flatMap((point: FVector) => [point.x, point.y, point.z]);

        const mesh = create2DPointsShape(positions, 5, false, 0x000000);
        mesh.frustumCulled = false;
        mesh.visible = true;
        return mesh;
    }

    private createMaterial(): THREE.RawShaderMaterial {
        const dummy = new THREE.Object3D();
        dummy.rotation.set(0, 0, THREE.MathUtils.degToRad(Grid.gridAngle));
        dummy.updateMatrix();

        return new THREE.RawShaderMaterial({
            uniforms: {
                opacity: { value: 1 },
                instR: { value: dummy.quaternion },
                Spin2: { value: false },
                multi: { value: false },
            },
            polygonOffset: true,
            polygonOffsetFactor: 0.4,
            polygonOffsetUnits: 0.4,
            vertexShader: vertexShader,
            fragmentShader: voxelFragmentShader,
            transparent: false,
            depthWrite: true,
            side: THREE.DoubleSide,
        });
    }

    // Creates the directionless
    public createDirectionlessSphereMesh(
        visualizationElement: VisualizationElement
    ): THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial> {
        const geometry = this.createBaseGeometry(visualizationElement.geometry);
        geometry.instanceCount = 0;

        const material = this.createMaterial();
        this.applyShaders(material, visualizationElement.id);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.frustumCulled = false;

        this.directionlessSphereMesh = mesh;
        return mesh;
    }

    public createDirectionlessStrokesMesh(
        visualizationElement: VisualizationElement
    ): THREE.LineSegments<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial> {
        const geometry = this.createStrokeGeometry(visualizationElement.geometry);
        geometry.instanceCount = 0;

        const material = this.createMaterial();
        this.applyShaders(material, visualizationElement.id);

        const mesh = new THREE.LineSegments(geometry, material);
        mesh.frustumCulled = false;
        mesh.visible = visualizationElement.showStroke;

        this.directionlessStrokesMesh = mesh;
        return mesh;
    }

    private createSensorsMesh(
        visElementGeometry: THREE.InstancedBufferGeometry,
        material: THREE.RawShaderMaterial
    ): THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial> {
        const geometry = this.createBaseGeometry(visElementGeometry);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.frustumCulled = false;
        return mesh;
    }

    private createStrokesMesh(
        visualizationElement: VisualizationElement,
        material: THREE.RawShaderMaterial
    ): THREE.LineSegments<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial> {
        const geometry = this.createStrokeGeometry(visualizationElement.geometry);
        const mesh = new THREE.LineSegments(geometry, material);
        mesh.frustumCulled = false;
        mesh.visible = visualizationElement.showStroke;
        return mesh;
    }

    private createBaseGeometry(
        visElementGeometry: THREE.InstancedBufferGeometry,
        preserveAttributes?: THREE.InstancedBufferGeometry
    ): THREE.InstancedBufferGeometry {
        const geometry = new THREE.InstancedBufferGeometry().copy(visElementGeometry);

        const positionsArray = new Float32Array(
            Grid.notNullSensorPoints
                .map((sensor: SensorPoint) => sensor.getCenter())
                .flatMap((point: FVector) => [point.x, point.y, point.z])
        );
        const scaleArray = new Float32Array(this.sensorCount);
        const colorArray = new Float32Array(this.sensorCount * 3);
        const sensorVectorialValues = new Float32Array(this.sensorCount * 3);

        geometry.setAttribute(
            'instT',
            preserveAttributes?.getAttribute('instT') || new THREE.InstancedBufferAttribute(positionsArray, 3, true)
        );
        geometry.setAttribute('instC', preserveAttributes?.getAttribute('instC') || new THREE.InstancedBufferAttribute(colorArray, 3));
        geometry.setAttribute('instS', preserveAttributes?.getAttribute('instS') || new THREE.InstancedBufferAttribute(scaleArray, 1));
        geometry.setAttribute(
            'instDir',
            preserveAttributes?.getAttribute('instDir') || new THREE.InstancedBufferAttribute(sensorVectorialValues, 3)
        );

        return geometry;
    }

    private createStrokeGeometry(
        visElementGeometry: THREE.InstancedBufferGeometry,
        preserveAttributes?: THREE.InstancedBufferGeometry
    ): THREE.InstancedBufferGeometry {
        const edgesGeom = new THREE.EdgesGeometry(visElementGeometry);
        const geometry = this.createBaseGeometry(visElementGeometry, preserveAttributes);

        geometry.index = edgesGeom.index;
        geometry.setAttribute('position', edgesGeom.getAttribute('position'));
        geometry.setAttribute('isStroke', new THREE.BufferAttribute(new Float32Array([1]), 1));

        return geometry;
    }

    // Public API Methods

    /**
     * Updates the geometry of sensors and strokes, preserving existing attributes
     */

    public updateGeometry(visualizationElement: VisualizationElement): boolean {
        const previousScaleFactor = this.elementScaleFactor;
        this.elementScaleFactor = visualizationElement.scaleFactor ?? 1;

        if (this.currentVisElementId === visualizationElement.id) {
            if (previousScaleFactor !== this.elementScaleFactor) {
                this.rescaleInstanceSizes(previousScaleFactor, this.elementScaleFactor);
            }
            return false; // No change needed
        }

        console.log('FrameDraw: updating geometry');

        // Preserve old attributes
        const oldSensorGeometry = this.fillMesh.geometry;
        const oldStrokeGeometry = this.strokesMesh.geometry;

        // Create new geometries with preserved attributes
        const newSensorGeometry = this.createBaseGeometry(visualizationElement.geometry, oldSensorGeometry);
        const newStrokeGeometry = this.createStrokeGeometry(visualizationElement.geometry, oldStrokeGeometry);

        // Dispose old geometries
        oldSensorGeometry.dispose();
        oldStrokeGeometry.dispose();

        // Assign new geometries
        this.fillMesh.geometry = newSensorGeometry;
        this.strokesMesh.geometry = newStrokeGeometry;

        // Update shaders if needed
        this.updateShaders(visualizationElement.id);
        this.currentVisElementId = visualizationElement.id;
        console.log(this.fillMesh.visible && visualizationElement.showStroke);
        this.strokesMesh.visible =
            this.fillMesh.visible && this.shouldShowStrokes(visualizationElement.id, visualizationElement.showStroke);

        if (previousScaleFactor !== this.elementScaleFactor) {
            this.rescaleInstanceSizes(previousScaleFactor, this.elementScaleFactor);
        }

        return true;
    }

    // Apply the shader for the correct geometry to the directionless sensors:
    public setDirectionlessVisualizationElementId(visualizationElementId: VisualizationElementId): void {
        if (this.directionlessVisElementId === visualizationElementId) return;
        const visElement = VisualizationElements.getVisualizationElement(visualizationElementId);

        const oldFillGeometry = this.directionlessSphereMesh.geometry;
        this.directionlessSphereMesh.geometry = this.createBaseGeometry(visElement.geometry, oldFillGeometry);
        oldFillGeometry.dispose();

        const oldStrokeGeometry = this.directionlessStrokesMesh.geometry;
        this.directionlessStrokesMesh.geometry = this.createStrokeGeometry(visElement.geometry, oldStrokeGeometry);
        oldStrokeGeometry.dispose();

        this.directionlessElementScaleFactor = visElement.scaleFactor ?? 1;
        this.directionlessVisElementId = visualizationElementId;

        this.applyShaders(this.directionlessSphereMesh.material, visualizationElementId);
        this.applyShaders(this.directionlessStrokesMesh.material, visualizationElementId);
        this.directionlessStrokesMesh.visible = this.shouldShowStrokes(visElement.id, visElement.showStroke);
    }

    public resetScale() {
        const scaleArray = new Float32Array(this.sensorCount);
        this.fillMesh.geometry.attributes.instS.array.set(scaleArray);
        this.fillMesh.geometry.attributes.instS.needsUpdate = true;

        this.strokesMesh.geometry.attributes.instS.array.set(scaleArray);
        this.strokesMesh.geometry.attributes.instS.needsUpdate = true;
    }
    /**
     * Updates instance attributes (positions, colors, scales, directions)
     */
    public updateAttributes(index: number, direction: FVector, colors: THREE.Color, scale: number): void {
        const geometries = [this.fillMesh.geometry, this.strokesMesh.geometry];

        // This is outside loop because i dont want to color sensor strokes
        if (colors) {
            const instC = this.fillMesh.geometry.getAttribute('instC') as THREE.InstancedBufferAttribute;
            instC.setXYZ(index, colors.r, colors.g, colors.b);
            instC.needsUpdate = true;
        }

        geometries.forEach((geometry) => {
            if (scale) {
                const instS = geometry.getAttribute('instS') as THREE.InstancedBufferAttribute;
                instS.setX(index, scale * this.elementScaleFactor);
                instS.needsUpdate = true;
            }

            if (direction) {
                const instDir = geometry.getAttribute('instDir') as THREE.InstancedBufferAttribute;
                instDir.setXYZ(index, direction.x, direction.y, direction.z);
                instDir.needsUpdate = true;
            }
        });
    }

    // Handles the switch between geometries with different scaleFactors.
    private rescaleInstanceSizes(previousScaleFactor: number, nextScaleFactor: number): void {
        if (previousScaleFactor === 0 || previousScaleFactor === nextScaleFactor) return;
        const factor = nextScaleFactor / previousScaleFactor;
        const geometries = [this.fillMesh.geometry, this.strokesMesh.geometry];

        geometries.forEach((geometry) => {
            const instS = geometry.getAttribute('instS') as THREE.InstancedBufferAttribute;
            const array = instS.array as Float32Array;
            for (let i = 0; i < array.length; i++) {
                array[i] *= factor;
            }
            instS.needsUpdate = true;
        });
    }

    /**
     * Shows or hides sensors and strokes
     */
    public showSensors(show: boolean): void {
        this.sensorsVisible = show;
        this.sensorMesh.visible = show;
        this.directionlessSphereMesh.visible = show;
        const currentVisElement = VisualizationElements.getVisualizationElement(this.currentVisElementId as VisualizationElementId);
        const visElement = VisualizationElements.getVisualizationElement(this.directionlessVisElementId as VisualizationElementId);
        this.strokesMesh.visible = show && this.shouldShowStrokes(currentVisElement.id, currentVisElement.showStroke);
        this.directionlessStrokesMesh.visible = show && this.shouldShowStrokes(visElement.id, visElement.showStroke);
    }

    /**
     * Shows or hides strokes (Voxel mode only)
     */
    public setNoStrokes(noStrokes: boolean): void {
        this.noStrokes = noStrokes;
        const currentVisElement = VisualizationElements.getVisualizationElement(this.currentVisElementId as VisualizationElementId);
        const directionlessVisElement = VisualizationElements.getVisualizationElement(
            this.directionlessVisElementId as VisualizationElementId
        );

        this.strokesMesh.visible = this.sensorsVisible && this.shouldShowStrokes(currentVisElement.id, currentVisElement.showStroke);
        this.directionlessStrokesMesh.visible =
            this.sensorsVisible && this.shouldShowStrokes(directionlessVisElement.id, directionlessVisElement.showStroke);
    }

    /**
     * Shows or hides black dots
     */
    public showBlackDots(show: boolean): void {
        this.dotMesh.visible = show;
    }

    /**
     * Updates material uniforms (Spin2, multi, opacity, etc.)
     */
    public updateMaterialUniforms(uniforms: { spin2?: boolean; multi?: boolean; opacity?: number; transparent?: boolean }): void {
        const material = this.fillMesh.material;

        if (uniforms.spin2 !== undefined) {
            material.uniforms.Spin2.value = uniforms.spin2;
        }

        if (uniforms.multi !== undefined) {
            material.uniforms.multi.value = uniforms.multi;
        }

        if (uniforms.opacity !== undefined) {
            material.uniforms.opacity.value = uniforms.opacity;
            this.directionlessSphereMesh.material.uniforms.opacity.value = uniforms.opacity;
            this.directionlessStrokesMesh.material.uniforms.opacity.value = uniforms.opacity;
        }

        if (uniforms.transparent !== undefined) {
            material.transparent = uniforms.transparent;
            this.directionlessSphereMesh.material.transparent = uniforms.transparent;
            this.directionlessStrokesMesh.material.transparent = uniforms.transparent;
        }

        material.needsUpdate = true;
        this.directionlessSphereMesh.material.needsUpdate = true;
        this.directionlessStrokesMesh.material.needsUpdate = true;
    }

    /**
     * Updates black dots positions and scale
     */
    public updateBlackDots(positions?: number[], size?: number): void {
        if (positions) {
            const positionsArray = new Float32Array(positions);
            this.dotMesh.geometry.setAttribute('position', new THREE.Float32BufferAttribute(positionsArray, 3));
            this.dotMesh.geometry.attributes.position.needsUpdate = true;
        }

        if (size !== undefined) {
            (this.dotMesh.material as THREE.PointsMaterial).size = size;
            this.dotMesh.material.needsUpdate = true;
        }
    }

    public updateDirectionlessInstances(items: { position: FVector; color: THREE.Color; scale: number }[]): void {
        const count = items.length;

        const fillGeometry = this.directionlessSphereMesh.geometry;
        const fillInstT = fillGeometry.getAttribute('instT') as THREE.InstancedBufferAttribute;
        const fillInstC = fillGeometry.getAttribute('instC') as THREE.InstancedBufferAttribute;
        const fillInstS = fillGeometry.getAttribute('instS') as THREE.InstancedBufferAttribute;

        const strokeGeometry = this.directionlessStrokesMesh.geometry;
        const strokeInstT = strokeGeometry.getAttribute('instT') as THREE.InstancedBufferAttribute;
        const strokeInstC = strokeGeometry.getAttribute('instC') as THREE.InstancedBufferAttribute;
        const strokeInstS = strokeGeometry.getAttribute('instS') as THREE.InstancedBufferAttribute;

        for (let i = 0; i < count; i++) {
            const item = items[i];
            const scale = item.scale * this.directionlessElementScaleFactor;
            fillInstT.setXYZ(i, item.position.x, item.position.y, item.position.z);
            fillInstC.setXYZ(i, item.color.r, item.color.g, item.color.b);
            fillInstS.setX(i, scale);

            strokeInstT.setXYZ(i, item.position.x, item.position.y, item.position.z);
            strokeInstC.setXYZ(i, 0, 0, 0);
            strokeInstS.setX(i, scale);
        }

        fillGeometry.instanceCount = count;
        strokeGeometry.instanceCount = count;
        fillInstT.needsUpdate = true;
        fillInstC.needsUpdate = true;
        fillInstS.needsUpdate = true;
        strokeInstT.needsUpdate = true;
        strokeInstC.needsUpdate = true;
        strokeInstS.needsUpdate = true;
    }

    private updateShaders(visualizationElementId: VisualizationElementId): void {
        console.log('FrameDraw: updating shaders');
        this.applyShaders(this.fillMesh.material, visualizationElementId);
        this.applyShaders(this.strokesMesh.material, visualizationElementId);
    }

    private shouldShowStrokes(visualizationElementId: VisualizationElementId, defaultShowStroke: boolean): boolean {
        if (!defaultShowStroke) return false;
        if (!this.noStrokes) return true;
        return visualizationElementId !== VisualizationElementId.Voxel;
    }

    private applyShaders(material: THREE.RawShaderMaterial, visualizationElementId: VisualizationElementId): void {
        let vertShader = vertexShader;
        let fragShader = voxelFragmentShader;

        switch (visualizationElementId) {
            case VisualizationElementId.Voxel:
            case VisualizationElementId.FlatVoxel:
                vertShader = voxelVertexShader;
                fragShader = voxelFragmentShader;
                break;
            case VisualizationElementId.Point:
            case VisualizationElementId.Quad:
                vertShader = vertexShader;
                fragShader = voxelFragmentShader;
                break;
            case VisualizationElementId.Box:
                vertShader = vertexShader;
                fragShader = boxFragmentShader;
                break;
        }

        material.vertexShader = vertShader;
        material.fragmentShader = fragShader;
        material.needsUpdate = true;
    }

    public makeTransparent(transparent: boolean, intensity: number = 0.5): void {
        this.updateMaterialUniforms({
            transparent,
            opacity: transparent ? intensity : 1,
        });
    }

    public getPickableSensorMeshes(): THREE.Object3D[] {
        return [this.sensorMesh, this.directionlessSphereMesh];
    }

    public dispose(): void {
        // Remove from scene
        getViewer().scene.remove(this.dotMesh, this.sensorMesh, this.directionlessSphereMesh, this.directionlessStrokesMesh);

        // Dispose geometries
        this.dotMesh.geometry.dispose();
        this.fillMesh.geometry.dispose();
        this.strokesMesh.geometry.dispose();
        this.directionlessSphereMesh.geometry.dispose();
        this.directionlessStrokesMesh.geometry.dispose();
        this.sensorMesh.clear();

        // Dispose materials
        const dotMaterial = this.dotMesh.material as THREE.PointsMaterial;
        if (dotMaterial.map) dotMaterial.map.dispose();
        dotMaterial.dispose();
        this.fillMesh.material.dispose();
        this.directionlessSphereMesh.material.dispose();
        this.directionlessStrokesMesh.material.dispose();
    }
}
